import { useEffect, useState } from 'react';

import type { DeliverySlot } from '~/api/ETA';
import { useDeliverySlotsInfo } from '~/components/ModalSlotsSelectDate/hooks';
import { compareLeadTime } from '~/components/ModalSlotsSelectDateCD/components';
import { useModal } from '~/hooks/useModal';

import { AnalyticsEvent, analyticsService } from '~/services/analytics';
import { cartStore, checkoutStore } from '~/stores';

export const useModalSlotSelectDate = () => {
  const { closeModal } = useModal();

  const { deliverySlots, currentDelivery } = useDeliverySlotsInfo();

  const timeStamps = Object.keys(deliverySlots);
  const [selectedDate, setSelectedDate] = useState<string | undefined>(
    timeStamps[0],
  );
  const [selectedSlotId, setSelectedSlotId] = useState<string | undefined>();
  const [shouldSplitSlot, setShouldSplitSlot] = useState<boolean>(false);

  useEffect(() => {
    if (selectedSlotId || selectedDate) {
      return;
    }

    const setFirstSlot = () => {
      const slot = checkoutStore.sortedDeliverySlotsList?.at(0);

      if (!slot) {
        return;
      }

      setSelectedDate(`${slot.current_date * 1000}`);
    };

    if (!currentDelivery) {
      setFirstSlot();
      return;
    }

    if (!currentDelivery.slot) {
      const slot = checkoutStore.sortedDeliverySlotsList?.at(0);

      if (!slot) {
        return;
      }
      setSelectedDate(`${slot.current_date * 1000}`);
      return;
    }

    const existedSlot =
      checkoutStore.slotSelectionModalStore.getSlotByIdAndDate(
        currentDelivery.slot.schedule_slot_id,
        `${currentDelivery.slot.current_date}`,
      );

    if (!existedSlot) {
      setFirstSlot();
      return;
    }

    setSelectedDate(`${existedSlot.current_date * 1000}`);
  }, []);

  const onChangeSlotId = (
    slotId: string,
    hasUnavailableProducts: boolean = false,
  ) => {
    setShouldSplitSlot(hasUnavailableProducts);
    setSelectedSlotId(slotId);
  };

  const handleSplit = () => {
    if (!checkoutStore.deliveries || !currentDelivery) {
      throw new Error('Something went wrong');
    }

    if (!selectedDate || !selectedSlotId) {
      throw new Error('Something went wrong');
    }

    const slot = deliverySlots[selectedDate]?.find(
      (slot) => slot.schedule_slot_id === selectedSlotId,
    ) as DeliverySlot;

    const unavailableProducts = cartStore.selectedCartItems.filter(
      ({ backorderLeadTime }) =>
        compareLeadTime(slot.delivery_min_time, backorderLeadTime),
    );

    if (
      unavailableProducts.length &&
      checkoutStore.isMultipleDeliveriesAvailable &&
      checkoutStore.isDeliverTogether
    ) {
      checkoutStore.slotSelectionModalStore.setSelectedSlotForSplit(
        selectedSlotId,
      );
      checkoutStore.switchDeliverTogether();
      closeModal();
      return;
    }
  };

  const handleSelectSlot = () => {
    if (!checkoutStore.deliveries || !currentDelivery) {
      throw new Error('Something went wrong');
    }

    if (!selectedDate || !selectedSlotId) {
      throw new Error('Something went wrong');
    }

    if (
      currentDelivery.slotDeliveryDetails?.scheduleSlotId === selectedSlotId
    ) {
      checkoutStore.slotSelectionModalStore.removeAutomaticDelivery(
        selectedSlotId,
      );
      checkoutStore.slotSelectionModalStore.setSelectedSlot(null, null);
      closeModal();
      return;
    }

    checkoutStore.slotSelectionModalStore.updateDelivery(
      selectedSlotId,
      selectedDate,
    );
    closeModal();
    checkoutStore.slotSelectionModalStore.setSelectedSlot(null, null);

    analyticsService.track(AnalyticsEvent.DELIVERY_METHOD_SELECTED, {
      type: 'Slot',
    });
  };

  return {
    selectedDate,
    selectedSlotId,
    shouldSplitSlot,
    timeStamps,
    deliverySlots,
    currentDelivery,
    setSelectedDate,
    setSelectedSlotId,
    onChangeSlotId,
    handleSelectSlot,
    handleSplit,
    setShouldSplitSlot,
    closeModal,
  };
};
